<template>
    <v-container fluid>
        <v-row>
            <v-col class="text-right">
                <v-btn v-if="!inEditMode" class="ml-2 mr-2" color="primary" @click="editClick()">
                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="mb-2 text-h6 text-left ml-8">{{ $t('labels.billingListInformation') }}</v-col>
            <v-col class="text-right">
                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="saveDraft()">
                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.saveDraft') }}
                </v-btn>
                <v-btn v-if="inEditMode" class="mr-2" color="green" @click="confirmList()">
                    <v-icon small>fas fa-check</v-icon>&nbsp;{{ $t('buttons.confirmList') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="text-right">
                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="orange" @click="loadItemsDialog = true">
                    <v-icon small>fas fa-download</v-icon>&nbsp;{{ $t('buttons.loadItems') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="3" class="ml-4">
                <v-col cols="12">
                    <InputField
                        :readonly="!inEditMode"
                        :label="$t('fields.name')"
                        v-model="billingList.name"
                    ></InputField>
                </v-col>
            </v-col>
            <v-col cols="2">
                <v-col cols="12" class="text-center">
                    <Dropdown
                        v-model="billingList.invoice_to"
                        :readonly="true"
                        :ajax="true"
                        :data="'entities'"
                        :label="$t('fields.invoicetoEntity')"
                    ></Dropdown>
                </v-col>
            </v-col>
            <v-col cols="6">
                <v-col cols="12">
                    <TextArea
                        :readonly="!inEditMode"
                        :maxlength="500"
                        :height="100"
                        :label="$t('fields.information')"
                        v-model="billingList.information"
                    >
                    </TextArea>
                </v-col>
            </v-col>
            <v-row>
                <v-col> </v-col>
            </v-row>
        </v-row>

        <span v-for="(value, index) in positions" :key="index">
            <v-row>
                <v-col cols="12" class="mb-2 text-h6 ml-8">
                    {{ $t('labels.vesselName') }} {{ value.maritime_object }}</v-col
                >

                <v-col cols="12" class="mb-2 text-h7 ml-8"> {{ $t('labels.seafarerName') }}{{ value.seafarer }}</v-col>
            </v-row>
            <v-row>
                <v-col class="mb-2 ml-8 mr-8">
                    <v-data-table
                        :headers="objectHeaders"
                        :items="items"
                        fixed-header
                        dense
                        class="row-item"
                        :items-per-page="-1"
                        :hide-default-footer="true"
                        :no-data-text="$t('labels.noData')"
                    >
                        <template v-slot:item="row">
                            <tr :class="{'cursor-pointer': true}" v-if="row.item.position == value.position">
                                <td>
                                    <v-simple-checkbox v-model="row.item.invoice" disabled></v-simple-checkbox>
                                </td>
                                <td>
                                    <v-simple-checkbox v-model="row.item.payslip" disabled></v-simple-checkbox>
                                </td>
                                <td>
                                    {{ row.item.seafarer_display }}
                                </td>
                                <td>
                                    {{ row.item.rank_display }}
                                </td>
                                <td class="font-weight-bold">
                                    {{ row.item.item_display }}
                                </td>
                                <td>
                                    {{ row.item.details }}
                                </td>
                                <td>
                                    {{ row.item.date_display }}
                                </td>
                                <td class="text-right">
                                    {{ row.item.quantity_display }}
                                </td>

                                <td class="text-right">
                                    {{ row.item.rate_display }}
                                </td>
                                <td class="font-weight-bold text-right">
                                    {{ row.item.sum_display }}
                                </td>
                                <td class="text-right">
                                    {{ row.item.vat_display }}
                                </td>
                                <td class="text-right">
                                    {{ row.item.total_with_vat_display }}
                                </td>
                                <td>
                                    {{ row.item.currency_display }}
                                </td>
                                <td v-if="inEditMode">
                                    <div>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{on, attrs}">
                                                <v-btn icon v-bind="attrs" @click="removeItem(row.item.id)" v-on="on">
                                                    <v-icon small>fas fa-trash-alt</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('tooltips.delete') }}</span>
                                        </v-tooltip>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{on, attrs}">
                                                <v-btn
                                                    icon
                                                    color="red"
                                                    v-bind="attrs"
                                                    :loading="removingItemForever"
                                                    @click="removeItemForever(row.item.id)"
                                                    v-on="on"
                                                >
                                                    <v-icon small>fas fa-trash-alt</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('tooltips.deleteForever') }}</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                                <td v-else></td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </span>

        <v-row>
            <v-dialog persistent v-model="loadItemsDialog" width="600">
                <v-card>
                    <v-card-title>
                        {{ $t('labels.loadItems') }}
                    </v-card-title>
                    <v-card-text>
                        <v-row dense wrap>
                            <v-col cols="12" class="text-center">
                                <Dropdown
                                    v-model="invoiceTo"
                                    :ajax="true"
                                    :data="'entities-with-billing-items'"
                                    :label="$t('fields.invoicetoEntity')"
                                ></Dropdown>
                            </v-col>
                        </v-row>
                        <v-row dense wrap>
                            <v-col
                                cols="12"
                                md="6"
                                class="text-right"
                                :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                            >
                                <v-btn color="orange" class="white--text" @click.stop="loadItems"
                                    >{{ $t('buttons.loadItems') }}
                                </v-btn>
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                                class="text-left"
                                :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                            >
                                <v-btn color="red" class="white--text" @click.stop="loadItemsDialog = false"
                                    >{{ $t('buttons.cancel') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-row>
    </v-container>
</template>

<script>
import InputField from './trinkets/InputField';
import BillingList from '../models/BillingList';
import TextArea from './trinkets/TextArea.vue';
import Dropdown from './trinkets/Dropdown';

export default {
    name: 'BillingListDraf',
    components: {InputField, TextArea, Dropdown},
    data: function () {
        return {
            billingList: new BillingList({id: this.$route.params.id}),
            initialLoad: false,
            inEditMode: false,
            lockKey: 'billing-list-draft',
            lockTimer: null,
            invoiceTo: null,
            loadItemsDialog: false,
            removingItemForever: false,
            items: [],
            positions: [],
            objectHeaders: [
                {text: this.$t('fields.inv'), sortable: false},
                {text: this.$t('fields.ps'), sortable: false},
                {text: this.$t('fields.seafarer'), sortable: false},
                {text: this.$t('fields.rank'), sortable: false},
                {text: this.$t('fields.item'), sortable: false},
                {text: this.$t('fields.details'), sortable: false},
                {text: this.$t('fields.date'), sortable: false},
                {text: this.$t('fields.qty'), sortable: false},
                {text: this.$t('fields.rate'), sortable: false},
                {text: this.$t('fields.sum'), sortable: false},
                {text: this.$t('fields.vatPercentageSimbol'), sortable: false},
                {text: this.$t('fields.total'), sortable: false},
                {text: this.$t('fields.currency'), sortable: false},
                {text: '', value: 'actions', type: '', sortable: false, allowFilter: false, width: '15%'},
            ],
        };
    },
    methods: {
        confirmList() {
            this.billingList.confirmed = true;
            this.saveDraft();
        },
        editClick() {
            let vm = this;
            this.$setLock(this, this.lockKey + '|0', (data) => {
                if (data.success) {
                    vm.inEditMode = true;
                    vm.lockTimer = setInterval(() => {
                        vm.$setLock(vm, vm.lockKey + '|' + vm.entity.id);
                    }, 10000);
                } else {
                    vm.$alert(
                        this.$t('alerts.recordLocked')
                            .split('%name%')
                            .join(data.locked_by.first_name + ' ' + data.locked_by.last_name),
                    );
                }
            });
        },
        saveDraft() {
            let succes = true;
            if (this.billingList.name == '' || this.billingList.name == null) {
                succes = false;
                this.$alert(this.$t('alerts.insertName'));
            }
            if (this.billingList.invoice_to == null || this.billingList.invoice_to == '') {
                succes = false;
                this.$alert(this.$t('alerts.chooseEntityBeforeSavingBillingList'));
            }
            if (succes) {
                let vm = this;
                let action = 'post';
                let addition = '';
                if (vm.billingList.id != 0 && vm.billingList.id != null) {
                    action = 'put';
                    addition = '/' + vm.billingList.id;
                }
                let userId = this.$myStore.state.user.id;
                this.billingList.user_id = userId;
                let list = this.billingList;
                let items = this.items;
                this.$APICall(action, 'billing-lists-draft' + addition, {list, items}, (data, success) => {
                    if (success) {
                        vm.inEditMode = false;
                        vm.backClick();
                    } else {
                        vm.$alert(vm.$t('alerts.basicError'));
                    }
                });
            }
        },
        backClick() {
            this.$router.go(-1);
        },
        loadItems() {
            if (this.invoiceTo == null) {
                this.$alert(this.$t('alerts.selectEntity'));
            } else {
                let vm = this;
                if (this.billingList.id == null || this.billingList.id == 0) {
                    this.$APICall('get', 'items-for-billing/' + this.invoiceTo, {}, (data, success) => {
                        if (success) {
                            vm.items = data.items;
                            vm.positions = data.positions;
                            vm.billingList.invoice_to = vm.invoiceTo;
                            vm.loadItemsDialog = false;
                        }
                    });
                } else {
                    let invoice_to = this.invoiceTo;
                    this.$APICall(
                        'get',
                        'items-for-billing-for-billing-list/' + this.billingList.id,
                        {invoice_to},
                        (data, success) => {
                            if (success) {
                                vm.items = data.items;
                                vm.positions = data.positions;
                                vm.billingList.invoice_to = vm.invoiceTo;
                                vm.loadItemsDialog = false;
                            }
                        },
                    );
                }
            }
        },
        removeItem(itemId) {
            this.items.splice(
                this.items.findIndex((v) => v.id === itemId),
                1,
            );
        },

        removeItemForever(itemId) {
            this.$verify(this.$t('alerts.removeItemForeverQuestion'), (result) => {
                if (!result) {
                    return;
                }
                this.removingItemForever = true;
                this.$APICall('delete', 'item-for-billing/' + itemId, {}, (data, success) => {
                    if (success) {
                        this.$notify(this.$t('alerts.basicDelete'));
                        this.removeItem(itemId);
                    } else {
                        if (Object.hasOwn(data, 'custom')) {
                            this.$alert(data.message);
                        } else {
                            this.$alert(this.$t('alerts.basicError'));
                        }
                    }
                    this.removingItemForever = false;
                });
            });
        },
    },

    beforeRouteLeave(to, from, next) {
        if (this.inEditMode) {
            let vm = this;
            this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
                if (result) {
                    if (vm.lockTimer !== null) clearInterval(vm.lockTimer);
                    vm.$removeLock(vm, vm.lockKey + '|' + vm.billingList.id);
                    next(true);
                } else {
                    next(false);
                }
            });
        } else {
            if (this.lockTimer !== null) clearInterval(this.lockTimer);
            this.$removeLock(this, this.lockKey + '|' + this.billingList.id);
            next(true);
        }
    },
    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
        }
        let vm = this;
        if (this.billingList.id != 0) {
            this.$APICall('get', 'billing-list/' + this.billingList.id, {}, (data, success) => {
                if (success) {
                    let usableData = JSON.parse(JSON.stringify(data));
                    vm.billingList = usableData.item;
                    vm.items = usableData.items;
                    vm.positions = usableData.positions;
                    vm.$nextTick(() => {
                        vm.initialLoad = true;
                    });
                }
            });
        } else {
            this.inEditMode = true;
            this.initialLoad = true;
        }
    },
};
</script>

<style scoped></style>
